import { useRef, useEffect, useState, useMemo } from 'react'
import { ComparisonComponent } from '../comparison-component';
import { PricingComponent } from '../pricing-component';
import { Background, BackgroundVariant, ReactFlow, MarkerType, Handle, Position } from "@xyflow/react";
import '@xyflow/react/dist/style.css';
import { getStepIcon, setCookie, formatFiscalDate, getStatusBadge } from '../../utils/utils';
import { getWorkflowExecutionCount } from '../../api/thunks/workflows';
import { HeaderNavComponent } from '../header-nav-compontent';
import { MainBackground } from '../main-background';
import { edgeStyle, nodeTypes } from './react-flow';
import { createDemoAccount } from '../../api/thunks/users';
import { extractTrackingIds } from '../../utils/tracking';
import { useAppDispatch } from '../../store/hooks';
import { getLatestEvents, LatestEvent } from '../../api/thunks/financials';

export default function SplashPage9() {
    document.title = 'PocketQuant | Wall Street Intelligence for Main Street';
    const dispatch = useAppDispatch();
    const [workflowExecCount, setWorkflowExecCount] = useState<number | undefined>(undefined);
    const [isCreatingDemo, setIsCreatingDemo] = useState(false);
    const [latestEvents, setLatestEvents] = useState<LatestEvent[]>([]);
    const [isLoadingEvents, setIsLoadingEvents] = useState(true);

    // 1. Create refs for each section
    const tryItRef = useRef<HTMLDivElement>(null);
    const comparisonRef = useRef<HTMLDivElement>(null);
    const funkyHueRef = useRef<HTMLDivElement>(null);
    const pricingRef = useRef<HTMLDivElement>(null);

    // 2. Scroll to section on hash change
    useEffect(() => {
        const scrollToHash = () => {
            const hash = window.location.hash.replace('#', '');
            const refs: Record<string, React.RefObject<HTMLDivElement>> = {
                'try-it': tryItRef,
                'comparison': comparisonRef,
                'funky-hue': funkyHueRef,
                'pricing': pricingRef,
            };
            if (hash && refs && refs[hash]?.current) {
                refs[hash]!.current!.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };
        window.addEventListener('hashchange', scrollToHash);
        // Scroll on mount if hash exists
        if (window.location.hash) {
            setTimeout(scrollToHash, 100); // Ensure elements are rendered
        }
        return () => window.removeEventListener('hashchange', scrollToHash);
    }, []);

    useEffect(() => {
        if (!workflowExecCount) {
            getWorkflowExecutionCount().then((count) => {
                setWorkflowExecCount(count);
            }).catch((err) => {
                console.error("Error fetching workflow execution count:", err);
            })
        }
    }, [])

    useEffect(() => {
        // Fetch latest events on component mount
        getLatestEvents(4).then((response) => {
            setLatestEvents(response.events);
            setIsLoadingEvents(false);
        }).catch((err) => {
            console.error("Error fetching latest events:", err);
            setIsLoadingEvents(false);
        });
    }, [])

    const handleCreateDemo = () => {
        setIsCreatingDemo(true);
        
        // Extract tracking IDs
        const trackingIds = extractTrackingIds();
        
        // Create demo account
        createDemoAccount(dispatch, JSON.stringify(trackingIds)).then((result) => {
            // Set cookie to remember we created a demo account
            setCookie('demo-account-created', 'true', 1); // Expires in 1 day
            
            // Clear the signed-out flag if it exists
            setCookie('user-signed-out', '', -1);
            
            // Redirect to dashboard after successful demo account creation
            setTimeout(() => {
                window.location.href = '/dashboard';
            }, 1000); // Small delay to allow state to update
        }).catch((error: any) => {
            console.error('Failed to create demo account:', error);
            setIsCreatingDemo(false);
        });
    };

    // If we're creating a demo account, show loading
    if (isCreatingDemo) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600 mx-auto"></div>
                    <p className="mt-4 text-lg text-gray-600">Setting up your free demo account...</p>
                </div>
            </div>
        );
    }

    return (
        <MainBackground>
            <HeaderNavComponent/>
            <div className="relative isolate px-6 lg:px-8 pt-12">
                <div className="mx-auto max-w-7xl pt-20 pb-16 sm:pt-24 sm:pb-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center w-full relative">
                        {/* Left column with existing content */}
                        <div className="text-center md:text-left">
                            <div className="hidden sm:mb-8 md:flex">
                                <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                                    🚀 New: AI-powered earnings analysis & automated workflows
                                    <a href="/changelog" className="font-semibold text-orange-600">
                                        <span className="absolute inset-0" aria-hidden="true" /> See what's new <span aria-hidden="true">&rarr;</span>
                                    </a>
                                </div>
                            </div>
                            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                                Wall Street Intelligence
                            </h1>
                            <p className="mt-6 text-2xl leading-8 text-gray-600">
                                Built for Main Street 
                            </p>
                            <p className="leading-8 text-gray-600">
                                Get institutional-grade financial analysis at retail prices
                            </p>
                            
                            <div className="mt-10 flex items-center md:justify-start justify-center gap-x-6">
                                <button
                                    onClick={handleCreateDemo}
                                    disabled={isCreatingDemo}
                                    className="rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isCreatingDemo ? 'Creating Demo...' : 'Try Demo'}
                                </button>
                                {/* <a href="/learning-center" className="text-sm font-semibold leading-6 text-gray-900">
                                    See how it works <span aria-hidden="true">→</span>
                                </a> */}
                            </div>
                        </div>

                        {/* Right column with quick stats/features */}
                        <div className="hidden md:flex flex-col space-y-8">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200/50">
                                    <div className="flex items-center">
                                        <div className="h-8 w-8 bg-orange-600 rounded-lg flex items-center justify-center">
                                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.847a4.5 4.5 0 003.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-2xl font-bold text-gray-900">92% Accuracy</p>
                                            <p className="text-sm text-gray-600">On <a className='pointer text-blue-500 underline' target="_blank" href="https://github.com/patronus-ai/financebench/tree/main">FinanceBench</a></p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200/50">
                                    <div className="flex items-center">
                                        <div className="h-8 w-8 bg-orange-600 rounded-lg flex items-center justify-center">
                                            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-2xl font-bold text-gray-900">{workflowExecCount ? workflowExecCount.toLocaleString() : '0'}</p>
                                            <p className="text-sm text-gray-600">Workflows Run</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200/50">
                                    <div className="flex items-center">
                                        <div className="h-8 w-8 bg-orange-600 rounded-lg flex items-center justify-center">
                                            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0-1.125-.504-1.125-1.125V11.25a9 9 0 00-9-9z" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-2xl font-bold text-gray-900">XBRL</p>
                                            <p className="text-sm text-gray-600">Raw SEC data access</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200/50">
                                    <div className="flex items-center">
                                        <div className="h-8 w-8 bg-orange-600 rounded-lg flex items-center justify-center">
                                            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-2xl font-bold text-gray-900">2 Hours</p>
                                            <p className="text-sm text-gray-600">Saved per company</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Latest Events Section */}
                <div className="pt-16 md:pt-24">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="mx-auto max-w-2xl lg:text-center mb-16">
                            <h2 className="text-base font-semibold leading-7 text-orange-600">Real-time market intelligence</h2>
                            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                Latest Earnings Events
                            </p>
                            <p className="mt-6 text-lg leading-8 text-gray-600">
                                Stay ahead with the most recent earnings calls and reports, analyzed by our AI in real-time.
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                            {isLoadingEvents ? (
                                // Loading skeleton
                                Array.from({ length: 4 }).map((_, index) => (
                                    <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 animate-pulse">
                                        <div className="flex items-center space-x-4 mb-4">
                                            <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                                            <div>
                                                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                                                <div className="h-3 bg-gray-200 rounded w-16"></div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="h-4 bg-gray-200 rounded w-32"></div>
                                            <div className="h-3 bg-gray-200 rounded w-20"></div>
                                            <div className="h-3 bg-gray-200 rounded w-full"></div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                latestEvents.map((event) => {
                                    const badge = getStatusBadge(event.eventType, event.isTranscript);
                                    
                                    return (
                                        <div key={event.id} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
                                            <div className="flex items-center space-x-4 mb-4">
                                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center p-2">
                                                    <img 
                                                        src={`/company_icons/${event.ticker}.png`} 
                                                        alt={`${event.companyName} logo`} 
                                                        className="w-full h-full object-contain"
                                                    />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-gray-900">{event.companyName}</h3>
                                                    <p className="text-sm text-gray-600">{event.ticker}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-gray-700">{event.quarter} {event.eventType.replace('_', ' ')}</span>
                                                    <span className={`px-2 py-1 ${badge.className} text-xs font-medium rounded-full`}>
                                                        {badge.text}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500">Fiscal Date Ending: {formatFiscalDate(event.fiscalDateEnding)}</p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="text-center">
                            <a 
                                href="/events"
                                className="inline-flex items-center px-6 py-3 border border-orange-300 text-base font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 transition-colors"
                            >
                                View All Recent Events
                                <svg className="ml-2 -mr-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Features Section */}
                <div className="pt-32 md:pt-48">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="mx-auto max-w-2xl lg:text-center">
                            <h2 className="text-base font-semibold leading-7 text-orange-600">Stop missing opportunities</h2>
                            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                Professional-grade financial intelligence
                            </p>
                            <p className="mt-6 text-lg leading-8 text-gray-600">
                                Access the same data and AI analysis tools that hedge funds and institutional investors use—without the $100k+ price tag.
                            </p>
                        </div>
                        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                                <div className="flex flex-col">
                                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                        <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-orange-600">
                                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.847a4.5 4.5 0 003.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                            </svg>
                                        </div>
                                        Trustworthy AI Analysis
                                    </dt>
                                    <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                        <p className="flex-auto">
                                            Our AI achieves 92% accuracy on FinBench, the industry's leading financial AI benchmark. Get insights that are actually reliable, not hallucinations.
                                        </p>
                                    </dd>
                                </div>
                                <div className="flex flex-col">
                                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                        <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-orange-600">
                                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0-1.125-.504-1.125-1.125V11.25a9 9 0 00-9-9z" />
                                            </svg>
                                        </div>
                                        Raw Financial Data
                                    </dt>
                                    <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                        <p className="flex-auto">
                                            Skip the sanitized data from Yahoo Finance. Access raw XBRL filings and granular financial details that most platforms hide or don't have.
                                        </p>
                                    </dd>
                                </div>
                                <div className="flex flex-col">
                                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                        <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-orange-600">
                                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                                            </svg>
                                        </div>
                                        Smart Automation
                                    </dt>
                                    <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                        <p className="flex-auto">
                                            Create custom workflows that monitor earnings calls, SEC filings, and market events 24/7. Get instant alerts when opportunities match your criteria.
                                        </p>
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {/* Data Sources Section */}
                        <div className="mt-32">
                            <div className="mx-auto max-w-2xl lg:text-center">
                                <h3 className="text-base font-semibold leading-7 text-orange-600">Connected to what matters</h3>
                                <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                    Monitor the sources that move markets
                                </p>
                                <p className="mt-6 text-lg leading-8 text-gray-600">
                                    Get instant access to the data streams that institutional investors pay millions to monitor.
                                </p>
                            </div>
                            <div className="mx-auto mb-16 mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-4 lg:gap-8">
                                <div className="flex gap-x-4 rounded-xl bg-white p-6 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 transition-colors">
                                    <div className="text-base leading-7">
                                        <h3 className="font-semibold text-gray-900">Live Earnings Calls</h3>
                                        <p className="mt-2 text-gray-600">Real-time transcripts and AI analysis of management commentary, guidance, and Q&A insights.</p>
                                    </div>
                                </div>
                                <div className="flex gap-x-4 rounded-xl bg-white p-6 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 transition-colors">
                                    <div className="text-base leading-7">
                                        <h3 className="font-semibold text-gray-900">SEC Filings (Raw)</h3>
                                        <p className="mt-2 text-gray-600">Direct XBRL access to 10-K, 10-Q, 8-K filings. Get the data before it's processed by other platforms.</p>
                                    </div>
                                </div>
                                <div className="flex gap-x-4 rounded-xl bg-white p-6 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 transition-colors">
                                    <div className="text-base leading-7">
                                        <h3 className="font-semibold text-gray-900">Fed Announcements</h3>
                                        <p className="mt-2 text-gray-600">FOMC meetings, policy changes, and economic projections—analyzed for market impact in real-time.</p>
                                    </div>
                                </div>
                                <div className="flex gap-x-4 rounded-xl bg-white p-6 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 transition-colors">
                                    <div className="text-base leading-7">
                                        <h3 className="font-semibold text-gray-900">Executive Orders</h3>
                                        <p className="mt-2 text-gray-600">Presidential actions and policy announcements that create sector-specific opportunities and risks.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Add id and ref to each section */}
                {/* <div id="try-it" ref={tryItRef}>
                    <TryIt showNav={false} showTabs={false} />
                </div> */}

                <div id="comparison" ref={comparisonRef}>
                    <ComparisonComponent showNav={false} />
                </div>

                {/* <div id="funky-hue" ref={funkyHueRef}>
                    <FunkyHueCircleThing />
                </div> */}

                <div id="pricing" className='mt-12' ref={pricingRef}>
                    <PricingComponent showNav={false} />
                </div>
            </div>

            {/* Footer Section */}
            <footer className="bg-gray-100 py-6 mt-12">
                <div className="container mx-auto px-6 lg:px-8">
                    <div className="flex justify-center space-x-6">
                        <a href="/terms-of-service" className="text-sm text-gray-600 hover:text-gray-900">
                            Terms of Service
                        </a>
                        <a href="/privacy-policy" className="text-sm text-gray-600 hover:text-gray-900">
                            Privacy Policy
                        </a>
                        <a href="/anti-scraping" className="text-sm text-gray-600 hover:text-gray-900">
                            Anti-Scraping Policy
                        </a>
                    </div>
                </div>
            </footer>
        </MainBackground>
    )
}

function ReactFlowDiagram() {
    const [selectedWorkflow, setSelectedWorkflow] = useState('earnings-call');

    // Create a node using the custom component
    const selectorNode = useMemo(() => ({
        id: 'workflow-selector',
        type: 'selector',
        position: { x: 100, y: -150 },
        data: {
            selectedWorkflow,
            setSelectedWorkflow
        }
    }), [selectedWorkflow, setSelectedWorkflow]);

    // Dynamically create nodes based on selected workflow
    const workflowNodes = useMemo(() => {
        switch (selectedWorkflow) {
            case 'earnings-call':
                return [
                    {
                        id: '1',
                        type: 'input',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-source")}
                                    <span className="ml-2">Earnings Call</span>
                                </div>
                            )
                        },
                        position: { x: 50, y: 50 },
                        style: { background: '#f97316', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '2',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("llm")}
                                    <span className="ml-2">AI Answers Question</span>
                                </div>
                            )
                        },
                        position: { x: 250, y: 150 },
                        style: { background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '3',
                        type: 'output',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("email")}
                                    <span className="ml-2">Email Me</span>
                                </div>
                            )
                        },
                        position: { x: 450, y: 50 },
                        style: { background: '#10b981', color: 'white', border: 'none', borderRadius: '8px' }
                    }
                ];
            case 'key-figures':
                return [
                    {
                        id: '1',
                        type: 'input',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-source")}
                                    <span className="ml-2">TSLA 8-K</span>
                                </div>
                            )
                        },
                        position: { x: 50, y: 0 },
                        style: { background: '#f97316', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '2',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("decision")}
                                    <span className="ml-2">Is this a deliveries report?</span>
                                    <Handle type="source" position={Position.Right} id="decision-no" />
                                    <Handle type="source" position={Position.Bottom} id="decision-yes" />
                                </div>
                            )
                        },
                        position: { x: 250, y: 0 },
                        style: { background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '5',
                        data: {
                            label: (
                                <div >
                                    End
                                    <Handle type="target" position={Position.Left} id="terminal-left" />
                                </div>
                            )
                        },
                        position: { x: 450, y: 0 },
                        style: { background: '#FF0000', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '3',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-extraction")}
                                    <span className="ml-2">Extract deliveries</span>
                                </div>
                            )
                        },
                        position: { x: 50, y: 100 },
                        style: { background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '4',
                        type: 'output',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("api-destination")}
                                    <span className="ml-2">API Destination</span>
                                </div>
                            )
                        },
                        position: { x: 250, y: 100 },
                        style: { background: '#10b981', color: 'white', border: 'none', borderRadius: '8px' }
                    }
                ];

            case '8k-analysis':
                return [
                    {
                        id: '1',
                        type: 'input',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-source")}
                                    <span className="ml-2">8-K Filing</span>
                                </div>
                            )
                        },
                        position: { x: 150, y: 0 },
                        style: { background: '#f97316', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '2',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("llm")}
                                    <span className="ml-2">Risk Analysis</span>
                                </div>
                            )
                        },
                        position: { x: 350, y: 0 },
                        style: { background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '3',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("decision")}
                                    <span className="ml-2">Material Risk?</span>
                                </div>
                            )
                        },
                        position: { x: 150, y: 100 },
                        style: { background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '4',
                        type: 'output',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("email")}
                                    <span className="ml-2">Urgent Alert</span>
                                </div>
                            )
                        },
                        position: { x: 350, y: 100 },
                        style: { background: '#10b981', color: 'white', border: 'none', borderRadius: '8px' }
                    }
                ];

            default:
                return [];
        }
    }, [selectedWorkflow]);

    // Get workflow edges based on the selected workflow
    const workflowEdges = useMemo(() => {
        switch (selectedWorkflow) {
            case 'earnings-call':
                return [
                    { id: 'e1-2', source: '1', target: '2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-3', source: '2', target: '3', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                ];
            case 'key-figures':
                return [
                    { id: 'e1-2', source: '1', target: '2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-3', source: '2', target: '3', label: "Yes", sourceHandle: "decision-yes", animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e3-4', source: '3', target: '4', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-5', source: '2', target: '5', label: "No", sourceHandle: "decision-no", targetHandle: "terminal-left", animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                ];
            case '8k-analysis':
                return [
                    { id: 'e1-2', source: '1', target: '2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-3', source: '2', target: '3', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e3-4', source: '3', target: '4', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                ];
            default:
                return [];
        }
    }, [selectedWorkflow]);

    // Combine workflow nodes with selector node
    const nodes = useMemo(() => [selectorNode, ...workflowNodes], [selectorNode, workflowNodes]);
    
    const edges = useMemo(() => workflowEdges.map(edge => ({
        ...edge,
        style: edgeStyle,
    })), [workflowEdges]);


    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            fitView
            nodeTypes={nodeTypes}
        >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        </ReactFlow>
    );
}

